import { computed, DOCUMENT, effect, Injectable, signal, inject } from "@angular/core";
import { getLoggerFor } from "@tylertech/logging";
import { LoginViewModel } from "@tylertech/tyler-api-types";
import { SecurityService } from "./security.service";
import { WINDOW } from "@tylertech/shared-module";

export type Theme = "light" | "dark";

export type ChartStyle = "color" | "borderColor" | "legendLabelColor" | "tooltipBodyColor" | "tooltipBackgroundColor" | "tooltipBorderColor" | "tooltipTitleColor" | "titleColor" | "pieChartBorderColor" | "trendLineColor";

type HexColor = `#${string}`;
type RgbaColor = `rgb(${number},${number},${number},${number})`;
type RgbColor = `rgb(${number},${number},${number})`;
export type ThemeColor = HexColor | RgbaColor | RgbColor;

const colorSeries: Record<Theme, ReadonlyArray<ThemeColor>> = {
    light: [
        "#008F8C",
        "#0D2644",
        "#E36039",
        "#154809",
        "#7a8ca1",
        "#641313",
        "#a69d4d",
        "#5C4170",
        "#ff656b",
        "#003D3C",

        "#006664",
        "#071322",
        "#D6491F",
        "#1A4012",
        "#4C5B6B",
        "#450D0D",
        "#756F2F",
        "#4A345B",
        "#AE3232",
        "#001414",
    ],
    dark: [
        "#8fdfdb",
        "#5a769e",
        "#ffc3a1",
        "#508344",
        "#c8d0e0",
        "#af5e5e",
        "#dcd597",
        "#7e688e",
        "#ffc0d6",
        "#008280",

        "#4cb684",
        "#5B95DD",
        "#e6754f",
        "#729E65",
        "#8A949F",
        "#BB8580",
        "#938d5b",
        "#9E8DAA",
        "#c6635b",
        "#7F9797",
    ]
};

const chartStyleColors: Record<Theme, {[key: string]: string}> = {
    light: {
        color: "#666666",
        borderColor: "#e0e0e0",
        legendLabelColor: "#212121",
        pieChartBorderColor: "#ffffff",
        tooltipBodyColor: "#2c2c2c",
        tooltipBackgroundColor: "#ffffff",
        tooltipBorderColor: "#e0e0e0",
        tooltipTitleColor: "#2c2c2c",    
        titleColor: "#212121",    
        trendLineColor: "#C63D3D"
    },
    dark: {
        color: "#ababab",
        borderColor: "#424242",
        legendLabelColor: "#e4e4e4",
        pieChartBorderColor: "#343434",
        tooltipBodyColor: "#ffffff",
        tooltipBackgroundColor: "#343434",
        tooltipBorderColor: "#424242",
        tooltipTitleColor: "#ffffff",    
        titleColor: "#e4e4e4",    
        trendLineColor: "#d95656"
    },
};

@Injectable()
export class ThemeService
{
    private readonly window = inject<Window>(WINDOW);
    private readonly document = inject<Document>(DOCUMENT);

    private readonly log = getLoggerFor("ThemeService");
    private readonly mql: MediaQueryList;

    private readonly internalTheme = signal<Theme>("light");
    readonly theme = this.internalTheme.asReadonly();
    readonly isDarkTheme = computed(() =>
    {
        return this.internalTheme() === "dark";
    });

    readonly colorSeries = computed(() =>
    {
        return [...colorSeries[this.theme()]];
    });

    readonly chartStyleColors = computed(() =>
    {
        return { ...chartStyleColors[this.theme()] };
    });

    private readonly mqlListenerChange = (e: MediaQueryListEvent): void =>
    {
        return this.internalTheme.set(e.matches ? "dark" : "light");
    };

    constructor()
    {
        const $securityService = inject(SecurityService);

        this.mql = this.window.matchMedia("(prefers-color-scheme: dark)");

        $securityService.principleChange
            .subscribe(
                (newPrinciple: LoginViewModel) =>
                {
                    if (!newPrinciple?.Appearance || newPrinciple.Appearance === "system")
                    {
                        this.internalTheme.set(this.mql?.matches ? "dark" : "light");

                        this.mql.removeEventListener("change", this.mqlListenerChange);
                        this.mql.addEventListener("change", this.mqlListenerChange);
                    }
                    else
                    {
                        this.mql.removeEventListener("change", this.mqlListenerChange);
                        this.internalTheme.set(newPrinciple.Appearance as Theme);
                    }
                }
            );

        effect(() =>
        {
            const isDarkTheme = this.isDarkTheme();

            this.document.body.classList.toggle("dark-theme", isDarkTheme);
        });
    }
};